// src/utils/validation.js
// *****************************************************************************
// VALIDATION UTILITIES
// *****************************************************************************
// Comprehensive validation utilities for Brazilian documents and common formats
// Provides validation for CPF, CNPJ, email, ZIP code, URL, and date formats
// Includes helper functions for normalizing and cleaning input data
// All validations follow Brazilian standards and best practices
// *****************************************************************************

// *****************************************************************************
// PUBLIC METHODS
// *****************************************************************************

/**
 * Remove all non-numeric characters from a string
 */
export const returnOnlyNumbers = str => {
  return str && str.toString().replace(/\D/g, '');
};

/**
 * Validate if value is a valid CPF or CNPJ
 */
export const isValidCpfCnpj = value => {
  if (!value) return false;

  if (returnOnlyNumbers(value).length === 14) {
    return isValidCnpj(value);
  } else if (returnOnlyNumbers(value).length === 11) {
    return isValidCpf(value);
  } else {
    return false;
  }
};

/**
 * Validate Brazilian CPF (Cadastro de Pessoas Físicas)
 */
export const isValidCpf = cpf => {
  if (!cpf) return;

  cpf = cpf.toString().replace(/[^\d]+/g, '');
  if (cpf === '') return false;

  // remove invalids cpfs
  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  ) {
    return false;
  }

  // check the first digit
  let add = 0;
  for (let i = 0; i < 9; i++) {
    add += parseInt(cpf.charAt(i)) * (10 - i);
  }

  let rev = 11 - (add % 11);

  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9))) return false;

  // check the second digit
  add = 0;
  for (let i = 0; i < 10; i++) {
    add += parseInt(cpf.charAt(i)) * (11 - i);
  }

  rev = 11 - (add % 11);

  if (rev === 10 || rev === 11) rev = 0;

  return rev === parseInt(cpf.charAt(10));
};

/**
 * Validate Brazilian CNPJ (Cadastro Nacional da Pessoa Jurídica)
 */
export const isValidCnpj = cnpj => {
  if (typeof cnpj === 'undefined') return false;

  cnpj = returnOnlyNumbers(cnpj);

  if (
    cnpj === '' ||
    cnpj.length !== 14 ||
    cnpj === '00000000000000' ||
    cnpj === '11111111111111' ||
    cnpj === '22222222222222' ||
    cnpj === '33333333333333' ||
    cnpj === '44444444444444' ||
    cnpj === '55555555555555' ||
    cnpj === '66666666666666' ||
    cnpj === '77777777777777' ||
    cnpj === '88888888888888' ||
    cnpj === '99999999999999'
  ) {
    return false;
  }

  // check digits
  let length = cnpj.length - 2;
  let number = cnpj.slice(0, length);
  let digits = cnpj.slice(length);
  let sum = 0;
  let suffix = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += number.charAt(length - i) * suffix--;
    if (suffix < 2) {
      suffix = 9;
    }
  }

  let resultado = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (resultado.toString() !== digits.charAt(0)) {
    return false;
  }

  length = length + 1;
  number = cnpj.slice(0, length);
  sum = 0;
  suffix = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += number.charAt(length - i) * suffix--;
    if (suffix < 2) {
      suffix = 9;
    }
  }
  resultado = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return resultado.toString() === digits.charAt(1);
};

/**
 * Validate email address format
 */
export const isValidEmail = email => {
  const regex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email);
};

/**
 * Validate Brazilian ZIP code (CEP) format
 */
export const isValidZip = zip => {
  const z = returnOnlyNumbers(zip);
  if (!z || z.length !== 8) return false;

  const formattedStr = z.slice(0, -3) + '-' + z.slice(-3);

  const regex = /[0-9]{5}-[\d]{3}/;
  return regex.test(formattedStr);
};

/**
 * Validate URL format
 */
export const isValidUrl = website => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(website);
};

/**
 * Validate if value is a valid Date object
 */
export const isValidDate = date => {
  return date instanceof Date && !isNaN(date);
};

/**
 * Validate US EIN (Employer Identification Number) — 9-digit company tax ID
 * Format: XX-XXXXXXX (area code 2 digits + sequence 7 digits)
 * Area code (first 2 digits) cannot be 00.
 */
export const isValidEin = ein => {
  const digits = returnOnlyNumbers(ein);
  if (!digits || digits.length !== 9) return false;
  if (/^(\d)\1{8}$/.test(digits)) return false;
  if (digits.slice(0, 2) === '00') return false;
  return true;
};

/**
 * Validate Portuguese NIF/NIPC (9-digit tax ID)
 * Same check-digit algorithm for NIF (personal) and NIPC (companies)
 */
export const isValidNipc = nipc => {
  const digits = returnOnlyNumbers(nipc);
  if (!digits || digits.length !== 9) return false;
  if (/^(\d)\1{8}$/.test(digits)) return false;

  const weights = [9, 8, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, w, i) => acc + parseInt(digits[i]) * w, 0);
  const remainder = sum % 11;
  const checkDigit = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(digits[8]) === checkDigit;
};

// NIF individual: starts with 1-4, same checksum as NIPC
export const isValidNif = nif => {
  const digits = returnOnlyNumbers(nif);
  if (!digits || digits.length !== 9) return false;
  if (!/^[1-4]/.test(digits)) return false;

  const weights = [9, 8, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, w, i) => acc + parseInt(digits[i]) * w, 0);
  const remainder = sum % 11;
  const checkDigit = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(digits[8]) === checkDigit;
};

// SSN: 9 digits, area not 000/666/9xx, group not 00, serial not 0000
export const isValidSsn = ssn => {
  const digits = returnOnlyNumbers(ssn);
  if (!digits || digits.length !== 9) return false;
  if (/^(\d)\1{8}$/.test(digits)) return false;
  const area = digits.slice(0, 3);
  if (area === '000' || area === '666' || area[0] === '9') return false;
  if (digits.slice(3, 5) === '00') return false;
  if (digits.slice(5) === '0000') return false;
  return true;
};

// *****************************************************************************
// EXPORTS
// *****************************************************************************

export default {
  returnOnlyNumbers,
  isValidCpfCnpj,
  isValidCpf,
  isValidCnpj,
  isValidEmail,
  isValidZip,
  isValidUrl,
  isValidDate,
  isValidNipc,
  isValidNif,
  isValidEin,
  isValidSsn,
};

// *****************************************************************************
// HELPERS
// *****************************************************************************

// No internal helpers for this module
